/**
 * AdminCostsPanel — Envío 3 · Paso 7 (tanda 1): "Costos y margen" en USD.
 *
 * Tres vistas: Balance (resumen del período + agrupados), Generaciones
 * (tabla con detalle por fila y exportación CSV) y Precios (editor de
 * model_pricing: un precio vigente no se edita, se cierra y se carga otro).
 *
 * Los filtros por período usan exec_created_at (fecha de la EJECUCIÓN).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DollarSign, RefreshCw, Loader2, Download, Tag, Table2, Wallet,
  TrendingDown, AlertTriangle, ChevronRight, ChevronDown, Plus, X, Coins,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';
import AdminDynamicPricingPanel from './AdminDynamicPricingPanel';

const PERIODS = [
  { id: '7d', label: '7 días', days: 7 },
  { id: '30d', label: '30 días', days: 30 },
  { id: '90d', label: '90 días', days: 90 },
  { id: 'all', label: 'Todo', days: null },
];

const DIMS = [
  { id: 'model', label: 'Modelo' },
  { id: 'result_type', label: 'Tipo de nodo' },
  { id: 'user', label: 'Usuario' },
  { id: 'cost_source', label: 'Origen del costo' },
  { id: 'month', label: 'Mes' },
];

const SOURCE_LABEL = {
  pricing_table: 'Medido en vivo',
  estimated: 'Estimado en vivo',
  estimated_backfill: 'Reconstruido (backfill)',
  no_price: 'Sin precio cargado',
  no_units: 'Sin unidades medibles',
  not_billed: 'No facturado (falló antes)',
};

export const usd = (v, digits = 4) => {
  const n = Number(v);
  if (v === null || v === undefined || Number.isNaN(n)) return '—';
  return `$${n.toFixed(digits)}`;
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es', {
    day: '2-digit', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const periodRange = (periodId) => {
  const p = PERIODS.find((x) => x.id === periodId) || PERIODS[1];
  // Sin rango el backend aplica su default de 30 días: «Todo» debe pedir
  // explícito desde el inicio de los tiempos, si no muestra sólo 30 días.
  if (!p.days) return { from: '2020-01-01T00:00:00Z' };
  const from = new Date(Date.now() - p.days * 86400000).toISOString();
  return { from };
};

// ── Balance ────────────────────────────────────────────────────────────────

function BalanceView({ period }) {
  const [data, setData] = useState(null);
  const [dim, setDim] = useState('model');
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const range = periodRange(period);
      const [sum, by] = await Promise.all([
        adminService.getCostsSummary(range),
        adminService.getCostsBy({ dim, ...range }),
      ]);
      setData(sum);
      setGrouped(by.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el balance');
    } finally {
      setLoading(false);
    }
  }, [period, dim]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="admin-costs-loading" data-testid="costs-balance-loading">
        <Loader2 size={18} className="animate-spin" /> Calculando el balance…
      </div>
    );
  }

  const s = data?.summary || {};
  const margin = Number(s.margin_usd ?? 0);
  const cards = [
    { k: 'revenue', label: 'Ingreso', value: usd(s.revenue_usd, 2), icon: Wallet },
    { k: 'cost', label: 'Costo', value: usd(s.cost_usd, 2), icon: DollarSign },
    {
      k: 'margin',
      label: 'Margen',
      value: usd(s.margin_usd, 2),
      icon: margin < 0 ? TrendingDown : Wallet,
      cls: margin < 0 ? 'negative' : 'positive',
    },
    {
      k: 'margin-pct',
      label: 'Margen %',
      value: s.margin_pct === null || s.margin_pct === undefined
        ? '—' : `${Number(s.margin_pct).toFixed(1)}%`,
      icon: Wallet,
    },
    { k: 'failures', label: 'Costo de fallas', value: usd(s.failure_cost_usd, 2), icon: AlertTriangle },
    { k: 'promo', label: 'Créditos promocionales', value: s.promo_credits_consumed ?? 0, icon: Tag },
  ];

  return (
    <div className="admin-costs-balance" data-testid="costs-balance">
      <div className="admin-costs-cards" data-testid="costs-balance-cards">
        {cards.map((c) => (
          <div
            key={c.k}
            className={`admin-costs-card ${c.cls || ''}`}
            data-testid={`costs-card-${c.k}`}
          >
            <span className="admin-costs-card-label"><c.icon size={12} /> {c.label}</span>
            <strong className="admin-costs-card-value">{c.value}</strong>
          </div>
        ))}
      </div>
      <p className="admin-costs-note" data-testid="costs-balance-execs">
        {s.executions ?? 0} generaciones en el período · todo en USD · período por fecha de ejecución
      </p>

      <div className="admin-costs-sources" data-testid="costs-by-source">
        <h4>Origen del costo</h4>
        <table className="admin-costs-table">
          <thead>
            <tr><th>Origen</th><th>Generaciones</th><th>Costo</th><th>Margen</th></tr>
          </thead>
          <tbody>
            {(data?.by_cost_source || []).map((r) => (
              <tr key={r.cost_source} data-testid={`costs-source-row-${r.cost_source}`}>
                <td>{SOURCE_LABEL[r.cost_source] || r.cost_source}</td>
                <td>{r.n}</td>
                <td>{usd(r.cost_total)}</td>
                <td className={Number(r.margin_total) < 0 ? 'neg' : ''}>{usd(r.margin_total)}</td>
              </tr>
            ))}
            {!(data?.by_cost_source || []).length && (
              <tr><td colSpan={4}>Sin datos en el período</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="admin-costs-grouped" data-testid="costs-grouped">
        <div className="admin-costs-grouped-head">
          <h4>Agrupado por</h4>
          <div className="admin-costs-dims">
            {DIMS.map((d) => (
              <button
                key={d.id}
                className={dim === d.id ? 'active' : ''}
                onClick={() => setDim(d.id)}
                data-testid={`costs-dim-${d.id}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
        <table className="admin-costs-table">
          <thead>
            <tr>
              <th>{DIMS.find((d) => d.id === dim)?.label}</th>
              <th>Generaciones</th><th>Costo total</th><th>Promedio</th>
              <th>Máximo</th><th>Ingreso</th><th>Margen</th><th>% margen neg.</th>
            </tr>
          </thead>
          <tbody>
            {grouped.map((r) => (
              <tr key={r.group_key} data-testid={`costs-group-row-${r.group_key}`}>
                <td className="mono">{r.group_key}</td>
                <td>{r.n}</td>
                <td>{usd(r.cost_total)}</td>
                <td>{usd(r.cost_avg)}</td>
                <td>{usd(r.cost_max)}</td>
                <td>{usd(r.revenue_total)}</td>
                <td className={Number(r.margin_total) < 0 ? 'neg' : ''}>{usd(r.margin_total)}</td>
                <td>{r.negative_margin_pct === null ? '—' : `${r.negative_margin_pct}%`}</td>
              </tr>
            ))}
            {!grouped.length && <tr><td colSpan={8}>Sin datos en el período</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Detalle de una generación ──────────────────────────────────────────────

function ExecutionDetail({ executionId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await adminService.getCostDetail(executionId);
        if (alive) setDetail(res);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'No se pudo cargar el detalle');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [executionId]);

  if (loading) {
    return (
      <div className="admin-costs-detail" data-testid="costs-detail-loading">
        <Loader2 size={14} className="animate-spin" /> Cargando detalle…
      </div>
    );
  }

  const c = detail?.cost || {};
  const ex = detail?.execution || {};
  const audit = detail?.audit;

  return (
    <div className="admin-costs-detail" data-testid={`costs-detail-${executionId}`}>
      <div className="admin-costs-detail-grid">
        <div>
          <h5>Cálculo</h5>
          <dl>
            <dt>Unidades</dt><dd>{c.units ?? '—'} ({c.billable_units?.unit || '—'})</dd>
            <dt>Fuente de unidades</dt><dd>{c.billable_units?.source || '—'}</dd>
            <dt>Precio unitario</dt><dd>{usd(c.unit_price_usd, 6)}</dd>
            <dt>Costo</dt><dd>{usd(c.cost_usd)}</dd>
            <dt>Origen</dt><dd>{SOURCE_LABEL[c.cost_source] || c.cost_source || '—'}</dd>
            <dt>Condiciones</dt>
            <dd className="mono">{JSON.stringify(c.billable_units?.conditions || {})}</dd>
          </dl>
        </div>
        <div>
          <h5>Créditos e ingreso</h5>
          <dl>
            <dt>Cobrados</dt><dd>{c.credits_charged ?? 0}</dd>
            <dt>Devueltos</dt><dd>{c.credits_refunded ?? 0}</dd>
            <dt>USD por crédito</dt><dd>{usd(c.usd_per_credit, 6)}</dd>
            <dt>Ingreso</dt><dd>{usd(c.revenue_usd)}</dd>
            <dt>Margen</dt>
            <dd className={Number(c.margin_usd) < 0 ? 'neg' : ''}>{usd(c.margin_usd)}</dd>
            <dt>Versión de cálculo</dt><dd>{c.calc_version ?? '—'}</dd>
          </dl>
        </div>
        <div>
          <h5>Ejecución</h5>
          <dl>
            <dt>Estado</dt><dd>{ex.status || c.exec_status || '—'}</dd>
            <dt>Intentos</dt><dd>{ex.attempts ?? '—'}</dd>
            <dt>Duración</dt>
            <dd>{ex.duration_seconds ? `${Number(ex.duration_seconds).toFixed(1)} s` : '—'}</dd>
            <dt>Categoría de error</dt><dd>{ex.error_category || '—'}</dd>
            <dt>Predicción</dt><dd className="mono">{c.prediction_id || '—'}</dd>
            <dt>Usuario</dt><dd className="mono">{c.user_id || '—'}</dd>
          </dl>
        </div>
      </div>

      {ex.error_message && (
        <div className="admin-costs-detail-block" data-testid="costs-detail-error">
          <h5>Error</h5>
          <pre>{ex.error_message}</pre>
        </div>
      )}

      {ex.metadata?.pricing && (
        <div className="admin-costs-detail-block" data-testid="costs-detail-pricing">
          <h5>Cotización (precio dinámico)</h5>
          <dl>
            <dt>Modo</dt><dd>{ex.metadata.pricing.mode}</dd>
            <dt>Cotizado</dt>
            <dd data-testid="costs-detail-quoted">
              {ex.metadata.pricing.credits} créditos
              {ex.metadata.pricing.mode === 'max' ? ' (reserva máxima)' : ''}
            </dd>
            <dt>Cobrado</dt>
            <dd data-testid="costs-detail-charged">{ex.credits_used ?? '—'} créditos</dd>
            <dt>Costo estimado</dt><dd>{usd(ex.metadata.pricing.cost_usd)}</dd>
            <dt>Costo real</dt><dd>{usd(c.cost_usd)}</dd>
            <dt>Motivo</dt><dd>{ex.metadata.pricing.reason || '—'}</dd>
          </dl>
        </div>
      )}

      {audit ? (
        <div className="admin-costs-detail-block" data-testid="costs-detail-audit">
          <h5>Auditoría del proveedor</h5>
          <pre>{JSON.stringify({
            input: audit.provider_input,
            output: audit.provider_output,
            error: audit.provider_error,
            logs: audit.provider_logs_tail,
          }, null, 2)}</pre>
        </div>
      ) : (
        <p className="admin-costs-note" data-testid="costs-detail-no-audit">
          Sin auditoría del proveedor (venció o la ejecución es previa al Paso 2b).
        </p>
      )}

      {!!detail?.events?.length && (
        <div className="admin-costs-detail-block" data-testid="costs-detail-events">
          <h5>Eventos</h5>
          <ul className="admin-costs-events">
            {detail.events.map((e) => (
              <li key={e.id}>
                <span className="mono">{fmtWhen(e.at)}</span>
                <strong>{e.kind}</strong>
                {e.attempt ? <em>intento {e.attempt}</em> : null}
                {e.error_category ? <em>{e.error_category}</em> : null}
                {e.message ? <span>{e.message}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Generaciones ───────────────────────────────────────────────────────────

function GenerationsView({ period }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({
    modelId: '', costSource: '', execStatus: '', onlyNegative: false,
  });
  const [exporting, setExporting] = useState(false);

  const query = useMemo(() => ({
    ...periodRange(period), ...filters, limit: 50, offset,
  }), [period, filters, offset]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.listCostExecutions(query);
      setRows(res.executions || []);
      setHasMore(!!res.has_more);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar las generaciones');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (patch) => {
    setOffset(0);
    setFilters((f) => ({ ...f, ...patch }));
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const blob = await adminService.downloadCostsCsv({
        ...periodRange(period), ...filters,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medialabs-costos-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
      toast.success('CSV descargado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo exportar el CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="admin-costs-generations" data-testid="costs-generations">
      <div className="admin-costs-filters" data-testid="costs-filters">
        <input
          type="text"
          placeholder="Filtrar por modelo (ej. google/nano-banana-2)"
          value={filters.modelId}
          onChange={(e) => setFilter({ modelId: e.target.value.trim() })}
          data-testid="costs-filter-model"
        />
        <select
          value={filters.costSource}
          onChange={(e) => setFilter({ costSource: e.target.value })}
          data-testid="costs-filter-source"
        >
          <option value="">Todos los orígenes</option>
          {Object.entries(SOURCE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={filters.execStatus}
          onChange={(e) => setFilter({ execStatus: e.target.value })}
          data-testid="costs-filter-status"
        >
          <option value="">Todos los estados</option>
          <option value="completed">Completadas</option>
          <option value="failed">Fallidas</option>
          <option value="canceled">Canceladas</option>
        </select>
        <label className="admin-costs-check">
          <input
            type="checkbox"
            checked={filters.onlyNegative}
            onChange={(e) => setFilter({ onlyNegative: e.target.checked })}
            data-testid="costs-filter-negative"
          />
          Sólo margen negativo
        </label>
        <button onClick={load} className="admin-refresh" data-testid="costs-refresh">
          <RefreshCw size={13} /> Actualizar
        </button>
        <button
          onClick={exportCsv}
          className="admin-costs-export"
          disabled={exporting}
          data-testid="costs-export-csv"
        >
          {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          Exportar CSV
        </button>
      </div>

      <table className="admin-costs-table" data-testid="costs-generations-table">
        <thead>
          <tr>
            <th />
            <th>Fecha</th><th>Tipo</th><th>Modelo</th><th>Unidades</th>
            <th>Costo</th><th>Ingreso</th><th>Margen</th><th>Origen</th><th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={10}><Loader2 size={14} className="animate-spin" /> Cargando…</td></tr>
          )}
          {!loading && !rows.length && (
            <tr><td colSpan={10}>Sin generaciones con esos filtros</td></tr>
          )}
          {!loading && rows.map((r) => {
            const open = expanded === r.execution_id;
            return [
              <tr key={r.execution_id} data-testid={`costs-row-${r.execution_id}`}>
                <td>
                  <button
                    className="admin-costs-expand"
                    onClick={() => setExpanded(open ? null : r.execution_id)}
                    data-testid={`costs-row-toggle-${r.execution_id}`}
                  >
                    {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  </button>
                </td>
                <td>{fmtWhen(r.exec_created_at)}</td>
                <td>{r.result_type || '—'}</td>
                <td className="mono">{r.model_id || '—'}</td>
                <td>{r.units ?? '—'}</td>
                <td>{usd(r.cost_usd)}</td>
                <td>{usd(r.revenue_usd)}</td>
                <td className={Number(r.margin_usd) < 0 ? 'neg' : ''}>{usd(r.margin_usd)}</td>
                <td><span className={`admin-costs-tag ${r.cost_source}`}>
                  {SOURCE_LABEL[r.cost_source] || r.cost_source}
                </span></td>
                <td>{r.exec_status || '—'}</td>
              </tr>,
              open && (
                <tr key={`${r.execution_id}-detail`} className="admin-costs-detail-row">
                  <td colSpan={10}><ExecutionDetail executionId={r.execution_id} /></td>
                </tr>
              ),
            ];
          })}
        </tbody>
      </table>

      <div className="admin-costs-pager" data-testid="costs-pager">
        <button
          onClick={() => setOffset(Math.max(0, offset - 50))}
          disabled={offset === 0 || loading}
          data-testid="costs-prev-page"
        >
          Anteriores
        </button>
        <span>filas {offset + 1}–{offset + rows.length}</span>
        <button
          onClick={() => setOffset(offset + 50)}
          disabled={!hasMore || loading}
          data-testid="costs-next-page"
        >
          Siguientes
        </button>
      </div>
    </div>
  );
}

// ── Precios ────────────────────────────────────────────────────────────────

const EMPTY_PRICE = {
  provider: 'replicate', model_id: '', billing_unit: 'per_image',
  unit_price_usd: '', conditions: '{}', source_url: '', notes: '',
};

const UNITS = [
  'per_image', 'per_output_second', 'per_input_second', 'per_output_file',
  'per_gpu_second', 'per_cpu_second', 'per_run', 'per_1k_chars',
  'per_mtok_in', 'per_mtok_out', 'per_mtok_cache_read', 'per_mtok_cache_write',
];

function PricingView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.listPricing({ includeHistory: history });
      setRows(res.pricing || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar los precios');
    } finally {
      setLoading(false);
    }
  }, [history]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    let conditions;
    try {
      conditions = JSON.parse(form.conditions || '{}');
    } catch {
      toast.error('Las condiciones deben ser JSON válido (ej. {"resolution":"1080p"})');
      return;
    }
    if (!form.model_id.trim()) {
      toast.error('Falta el model_id');
      return;
    }
    setSaving(true);
    try {
      await adminService.savePricing({
        provider: form.provider.trim(),
        model_id: form.model_id.trim(),
        billing_unit: form.billing_unit,
        unit_price_usd: form.unit_price_usd === '' ? null : Number(form.unit_price_usd),
        conditions,
        source_url: form.source_url || null,
        notes: form.notes || null,
      });
      toast.success('Precio cargado (el anterior quedó en el historial)');
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar el precio');
    } finally {
      setSaving(false);
    }
  };

  const close = async (id) => {
    try {
      await adminService.closePricing(id);
      toast.success('Precio cerrado');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cerrar el precio');
    }
  };

  return (
    <div className="admin-costs-pricing" data-testid="costs-pricing">
      <div className="admin-costs-pricing-head">
        <label className="admin-costs-check">
          <input
            type="checkbox"
            checked={history}
            onChange={(e) => setHistory(e.target.checked)}
            data-testid="costs-pricing-history"
          />
          Ver historial (precios cerrados)
        </label>
        <button
          className="admin-costs-export"
          onClick={() => setForm(form ? null : { ...EMPTY_PRICE })}
          data-testid="costs-pricing-new"
        >
          {form ? <X size={13} /> : <Plus size={13} />} {form ? 'Cancelar' : 'Cargar precio'}
        </button>
      </div>

      {form && (
        <div className="admin-costs-pricing-form" data-testid="costs-pricing-form">
          <input
            placeholder="provider (replicate)"
            value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
            data-testid="costs-pricing-provider"
          />
          <input
            placeholder="model_id (ej. bytedance/seedance-1-pro)"
            value={form.model_id}
            onChange={(e) => setForm({ ...form, model_id: e.target.value })}
            data-testid="costs-pricing-model"
          />
          <select
            value={form.billing_unit}
            onChange={(e) => setForm({ ...form, billing_unit: e.target.value })}
            data-testid="costs-pricing-unit"
          >
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <input
            type="number"
            step="0.000001"
            min="0"
            placeholder="USD por unidad"
            value={form.unit_price_usd}
            onChange={(e) => setForm({ ...form, unit_price_usd: e.target.value })}
            data-testid="costs-pricing-price"
          />
          <input
            placeholder='conditions JSON (ej. {"resolution":"1080p"})'
            value={form.conditions}
            onChange={(e) => setForm({ ...form, conditions: e.target.value })}
            data-testid="costs-pricing-conditions"
          />
          <input
            placeholder="source_url (opcional)"
            value={form.source_url}
            onChange={(e) => setForm({ ...form, source_url: e.target.value })}
            data-testid="costs-pricing-source"
          />
          <input
            placeholder="notas (opcional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="costs-pricing-notes"
          />
          <button onClick={save} disabled={saving} data-testid="costs-pricing-save">
            {saving ? <Loader2 size={13} className="animate-spin" /> : null} Guardar
          </button>
        </div>
      )}

      <table className="admin-costs-table" data-testid="costs-pricing-table">
        <thead>
          <tr>
            <th>Proveedor</th><th>Modelo</th><th>Unidad</th><th>Condiciones</th>
            <th>USD / unidad</th><th>Desde</th>{history && <th>Hasta</th>}<th />
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={8}><Loader2 size={14} className="animate-spin" /> Cargando…</td></tr>}
          {!loading && !rows.length && <tr><td colSpan={8}>Sin precios cargados</td></tr>}
          {!loading && rows.map((p) => (
            <tr key={p.id} data-testid={`costs-pricing-row-${p.id}`}>
              <td>{p.provider}</td>
              <td className="mono">{p.model_id}</td>
              <td>{p.billing_unit}</td>
              <td className="mono">{JSON.stringify(p.conditions || {})}</td>
              <td>{p.unit_price_usd === null ? 'sin precio' : usd(p.unit_price_usd, 6)}</td>
              <td>{fmtWhen(p.valid_from)}</td>
              {history && <td>{p.valid_to ? fmtWhen(p.valid_to) : 'vigente'}</td>}
              <td>
                {!p.valid_to && (
                  <button
                    className="admin-costs-close-price"
                    onClick={() => close(p.id)}
                    data-testid={`costs-pricing-close-${p.id}`}
                  >
                    Cerrar
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────────

export default function AdminCostsPanel() {
  const [view, setView] = useState('balance');
  const [period, setPeriod] = useState('30d');

  return (
    <div className="admin-costs-panel" data-testid="admin-costs-panel">
      <div className="admin-costs-head">
        <div className="admin-costs-title">
          <DollarSign size={15} />
          <span>Costos y margen</span>
          <em>todo en USD · el período filtra por fecha de ejecución</em>
        </div>
        <div className="admin-costs-periods" data-testid="costs-periods">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              className={period === p.id ? 'active' : ''}
              onClick={() => setPeriod(p.id)}
              data-testid={`costs-period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-costs-views" data-testid="costs-views">
        <button
          className={view === 'balance' ? 'active' : ''}
          onClick={() => setView('balance')}
          data-testid="costs-view-balance"
        >
          <Wallet size={13} /> Balance
        </button>
        <button
          className={view === 'generations' ? 'active' : ''}
          onClick={() => setView('generations')}
          data-testid="costs-view-generations"
        >
          <Table2 size={13} /> Generaciones
        </button>
        <button
          className={view === 'pricing' ? 'active' : ''}
          onClick={() => setView('pricing')}
          data-testid="costs-view-pricing"
        >
          <Tag size={13} /> Precios
        </button>
        <button
          className={view === 'dynamic' ? 'active' : ''}
          onClick={() => setView('dynamic')}
          data-testid="costs-view-dynamic"
        >
          <Coins size={13} /> Precio dinámico
        </button>
      </div>

      {view === 'balance' && <BalanceView period={period} />}
      {view === 'generations' && <GenerationsView period={period} />}
      {view === 'pricing' && <PricingView />}
      {view === 'dynamic' && <AdminDynamicPricingPanel />}
    </div>
  );
}
